import {
    BudgetGet,
    BudgetOpenItem,
    Decimal,
    SubscribeState,
    Ulid,
} from "@kase/my-marathon-communication";
import { ReadonlySignal, computed, signal } from "@preact/signals";
import { upsertOne } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createListController } from "../utils";
import {
    Budget,
    BudgetCreateRequest,
    BudgetLine,
    BudgetLineOpenItem,
    BudgetPhase,
    BudgetPhaseOpenItem,
} from "./interfaces";
import { makeLine } from "./line";
import { makePhase } from "./phase";

const STUB_SIGNAL = computed((): Budget[] => []);

export const makeBudget = ({ connection, company }: ApiSliceParams) => {
    const projectBudgets: Record<string, ReadonlySignal<Budget[]>> = {};
    const phase = makePhase({ connection, company });
    const line = makeLine({ connection, company });

    const {
        loading,
        loaded,
        listen,
        items: allBudgets,
    } = createListController<BudgetGet, Budget, string>({
        connection,
        list: (con, project) =>
            con.budget.items({
                company: company.value,
                project: Ulid.fromString(project),
            }),
        getKey: (project) => project,
        view: (budget) => view(budget),
        name: "budget",
    });

    const removeItems = (
        budget: Budget,
        items: readonly Readonly<Omit<BudgetOpenItem, "data">>[],
    ) => {
        const phases = items
            .filter((item) => item.t === "P")
            .map((item) => item.id.toString());
        const oldPhases: BudgetPhase[] = [];
        const newPhases: BudgetPhase[] = [];

        budget.phases.peek().forEach((phase) => {
            if (phases.includes(phase.id)) {
                oldPhases.push(phase);
            } else {
                newPhases.push(phase);
            }
        });

        budget.hangingLines.value = [
            ...budget.hangingLines.peek(),
            ...oldPhases.flatMap((t) => t.lines.peek()),
        ];

        if (newPhases.length !== budget.phases.peek().length) {
            budget.phases.value = newPhases;
        }

        items
            .filter((line) => line.t !== "P")
            .forEach((line) => {
                const phase = budget.phases.value.find((phase) =>
                    phase.lines.value.some((t) => t._id.equals(line.id)),
                );

                if (phase) {
                    phase.lines.value = phase.lines
                        .peek()
                        .filter((t) => !t._id.equals(line.id));
                } else {
                    budget.hangingLines.value = budget.hangingLines
                        .peek()
                        .filter((t) => !t._id.equals(line.id));
                }
            });
    };

    const addItems = (
        budget: Budget,
        items: readonly Readonly<BudgetOpenItem>[],
    ) => {
        const phases = items
            .filter((item): item is BudgetPhaseOpenItem => item.t === "P")
            .map((item) => ({
                ...item,
                _id: item.id,
                id: item.id.toString(),
                lines: signal<BudgetLine[]>([]),
            }));

        if (phases.length) {
            const newPhases = [...budget.phases.peek()];

            phases.forEach((phase) => {
                const existing = newPhases.find((t) => t._id.equals(phase._id));

                if (existing) {
                    Object.assign(existing.data, phase.data);
                } else {
                    newPhases.push(phase);
                }
            });

            budget.phases.value = newPhases.sort(
                (a, b) => (a.data.prio ?? 0) - (b.data.prio ?? 0),
            );
        }

        items
            .filter((line): line is BudgetLineOpenItem => line.t !== "P")
            .forEach((line) => {
                const oldPhase = budget.phases.value.find((phase) =>
                    phase.lines.value.some((t) => t._id.equals(line.id)),
                );
                const linePhaseId = line.data.phase;
                const phase = linePhaseId
                    ? budget.phases.value.find((phase) =>
                          phase._id.equals(linePhaseId),
                      )
                    : null;

                const finalLine = {
                    ...line,
                    _id: line.id,
                    id: line.id.toString(),
                };

                if (phase) {
                    phase.lines.value = upsertOne(
                        phase.lines.peek().slice(),
                        finalLine,
                        "id",
                    ).sort(
                        (a, b) =>
                            (a.data.position ?? 0) - (b.data.position ?? 0),
                    );

                    if (oldPhase && oldPhase.id !== phase.id) {
                        oldPhase.lines.value = oldPhase.lines.value.filter(
                            (t) => t.id !== finalLine.id,
                        );
                    } else if (!oldPhase) {
                        budget.hangingLines.value = budget.hangingLines
                            .peek()
                            .filter((t) => t.id !== finalLine.id);
                    }
                } else {
                    budget.hangingLines.value = upsertOne(
                        budget.hangingLines.peek().slice(),
                        finalLine,
                        "id",
                    ).sort(
                        (a, b) =>
                            (a.data.position ?? 0) - (b.data.position ?? 0),
                    );

                    if (oldPhase) {
                        oldPhase.lines.value = oldPhase.lines.value.filter(
                            (t) => t.id !== finalLine.id,
                        );
                    }
                }
            });
    };

    const open = async (budget: Budget) => {
        const con = connection.value;
        const sub = con.budget.open({
            company: company.value,
            id: budget._id,
        });

        budget.loading.value = true;

        for await (const event of sub) {
            if (event.kind === SubscribeState.Remove) {
                removeItems(budget, event.values);
            } else {
                addItems(budget, event.values);
            }

            if (event.kind === SubscribeState.Loaded) {
                budget.loading.value = false;
            }
        }
    };

    const view = (budget: BudgetGet) => {
        const finalBudget = {
            ...budget,
            id: budget.id.toString(),
            _id: budget.id,
            phases: signal([]),
            hangingLines: signal([]),
            loading: signal(false),
        };

        open(finalBudget);

        return finalBudget;
    };

    const create = async (request: BudgetCreateRequest) => {
        const con = connection.value;
        const comp = company.value;
        const result = await con.budget.create({
            company: comp,
            header: request.header,
            project: request.project,
            currency: request.currency,
            currency_rate: new Decimal(request.rate),
        });
        let defaultPhaseId: Readonly<Ulid> | null = null;

        if (request.defaultPhase) {
            defaultPhaseId = await phase.create({
                ...request.defaultPhase,
                budget: result,
            });
        }

        return [result, defaultPhaseId] as const;
    };

    const remove = (id: Readonly<Ulid>) => {
        return connection.value.budget.delete({ company: company.value, id });
    };

    const budgets = (id: string) => {
        if (!id) {
            return STUB_SIGNAL;
        }

        if (!(id in projectBudgets)) {
            projectBudgets[id] = computed(() =>
                allBudgets.value.filter(
                    (budget) => budget.project.toString() === id,
                ),
            );
        }

        return projectBudgets[id];
    };

    return { phase, line, budgets, loading, loaded, create, listen, remove };
};
